import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  InputGroupText,
  InputGroup,
} from 'reactstrap';
import i18n from '@/utils/i18n';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Communicator } from '../tools/Communication';
import { TransactionHistoryTable } from './BillingComponents/TransactionHistoryTable';
import { PageContainer, ResponsiveTable } from '../components/Layout';
import { FaUser } from 'react-icons/fa';
import { IconButton } from '../components/Button/Button';
import { costToPrice } from '@/utils/formatter';
import { ContentSection } from '../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  partialRefundOverduePenaltyBillMutation,
  cancelTransactionMutation,
} from '../graphql/mutations';
import { IndicatorButton } from '../components/IndicatorButton';
import {
  GetTransactionDocument,
  useGetTransactionQuery,
} from '../graphql/queries/getTransaction.generated';

export interface UserInfo {
  id: string;
  username: string;
}

interface IUrlParameters {
  page: undefined | string;
  showPaid: undefined | string;
  billId: undefined | string;
  type: undefined | string;
}

interface IPrice {
  amount: number;
  currencyId: number;
}

interface IContainerLocation {
  id: number;
  locationNumber?: number;
  containerId?: string;
  ownerSince: string;
  ownerId: string;
  nextOwnerId: string;
  container: IContainer;
  // Penalty-bill-only fields, populated by GetContainerLocationsForBill
  // when the bill is an OverduePenaltyBill.
  penaltyCost?: IPrice;
  refundedAt?: string | null;
}

interface IPayment {
  containerLocationId: number;
  timestamp: string;
  price: { amount: number };
}

interface IContainer {
  id: string;
  name: string;
  type: { id: number; name: string };
}

enum PaymentStatus {
  Unknown,
  Pending,
  WaitingForResponse,
  Paid,
  Failed,
  Refunded,
  PartiallyRefunded,
}

interface IPaymentData {
  timestamp: string | null;
  paymentIdentifier: string | null;
  paymentProviderConsumerInvoiceUrl: string | null;
  paymentProviderDashboardIvoiceUrl: string | null;
  paymentDataId: number | null;
  state: PaymentStatus;
  userPaymentData: null;
}

interface IBill {
  id: string;
  type: 'Subscription' | 'Penalty' | 'ContainerReturn' | 'DeadlineExtension';
  creationTimestamp: string;
  dueTimestamp: string | null;
  cost: IPrice;
  payment: IPaymentData;
}
interface ISubscriptionType {
  id: number;
  name: string;
  maxAmountOfCheckedOutBowls: number;
  rewardPointsMultiplier: number;
  monthlyFee: IPrice;
}

interface IUserSubscription {
  id: string;
  userId: string;
  paymentId: number;
  start: string;
  end: string;
  subscriptionTypeId: number;
}

interface IDetailedBill extends IBill {
  containerPaymentId: number;
  containerLocationId: number;
  pickpackTransactionId: string | null;
}

const pageLength = 50;

export const BillingView: React.FC = () => {
  const params = useParams<IUrlParameters>();
  const history = useHistory();

  // Determine title based on whether viewing a specific bill or the list
  const titleKey = params.billId ? 'PageTitles:bill' : 'PageTitles:billing';
  useDocumentTitle(titleKey, params.billId?.split('-').shift() || '');

  const [bills, setBills] = useState<IBill[]>([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState<{
    [id: number]: ISubscriptionType;
  }>({});
  const [lastPage, setLastPage] = useState<number>(0);
  const [lastShowPaid, setLastShowPaid] = useState<boolean>(false);
  const [users, setUsers] = useState<{ [id: string]: UserInfo }>({});
  const [billDetails, setBillDetails] = useState<IDetailedBill | null>(null);
  const [locations, setLocations] = useState<IContainerLocation[] | null>(null);
  const [paymentResponse, setPaymentResponse] = useState<string | null>(null);
  const [isInPaymentProcess, setIsInPaymentProcess] = useState<boolean>(false);
  const [billTypeFilter, setBillTypeFilter] =
    useState<string>('DeadlineExtension');
  // Per-container selection for partial refund. Keyed by `${containerId}:${locationNumber}`.
  const [selectedRefundKeys, setSelectedRefundKeys] = useState<Set<string>>(
    new Set(),
  );
  const [partialRefundOverduePenaltyBill, { loading: partialRefundLoading }] =
    useMutation(partialRefundOverduePenaltyBillMutation);

  // Whole-bill refund. The query is shared (deduped) with TransactionHistoryTable.
  const { data: transactionData } = useGetTransactionQuery({
    skip: !billDetails?.pickpackTransactionId,
    variables: { transaction_id: billDetails?.pickpackTransactionId || '' },
  });
  const [cancelTransaction, { loading: cancelingTransaction }] = useMutation(
    cancelTransactionMutation,
  );
  const transaction = transactionData?.getTransaction;
  const isTransactionRefunded =
    transaction?.state === 'CANCELLED' || !!transaction?.id?.startsWith('re_');

  const onRefundWholeBillClick = async () => {
    if (!transaction?.id || !billDetails) return;
    if (!window.confirm(i18n.t('Billing:confirmRefundTransaction'))) return;
    await cancelTransaction({
      variables: { transaction_id: transaction.id },
      refetchQueries: [
        {
          query: GetTransactionDocument,
          variables: { transaction_id: billDetails.pickpackTransactionId },
        },
      ],
    });
    reloadBillDetails(billDetails.id, billDetails.type);
  };

  const reloadLocationDetails = (billId: string) => {
    Communicator.Get('/Billing/GetContainerLocationsForBill', { billId })
      .then((response) => response.json())
      .then((data: { locations: IContainerLocation[] }) => {
        if (data && data.locations) {
          setLocations(data.locations);
        }
      });
  };

  const reloadBillDetails = (id: string, type: string) => {
    setBillDetails(null);
    setLocations(null);
    setSelectedRefundKeys(new Set());

    Communicator.Get('/Billing/GetBillDetails', { billType: type, id })
      .then((response) => response.json())
      .then((data: { bill: IDetailedBill; users: UserInfo[] }) => {
        if (data.bill && data.users) {
          setUsers((prevUsers) => {
            const newUsers = { ...prevUsers };
            data.users.forEach((u) => (newUsers[u.id] = u));
            return newUsers;
          });
          data.bill.type = type as any;
          setBillDetails(data.bill);
          reloadLocationDetails(data.bill.id);
        }
      });
  };

  const reloadSubscriptionTypes = () => {
    Communicator.Get('/Billing/GetSubscriptionTypes', {})
      .then((response) => response.json())
      .then((data: { types: ISubscriptionType[] }) => {
        if (data && data.types) {
          data.types.forEach((t) => (subscriptionTypes[t.id] = t));
          setSubscriptionTypes(subscriptionTypes);
        }
      });
  };

  const reloadBills = (page: number, includePaid: boolean, type: string) => {
    // Todo: Dropdown menu fpr bill type
    let billType = type as
      | 'Subscription'
      | 'Penalty'
      | 'ContainerReturn'
      | 'DeadlineExtension';
    Communicator.Get('/Billing/GetFeeBills', {
      type: billType,
      limit: pageLength,
      offset: pageLength * page,
      includePaid: includePaid,
      dueDateFilter: new Date(1, 1, 1970).toISOString(),
    })
      .then((response) => response.json())
      .then((data: { bills: IBill[] }) => {
        var bills: IBill[] = [];
        if (data && data.bills) {
          data.bills.forEach((b) => (b.type = billType));
          bills = data.bills;
        }
        setBills(bills);
      });
  };

  const changeBillTypeFilter = (type: string | null) => {
    if (type != null) {
      setBillTypeFilter(type);
      reloadBills(currentPage, showPaid, type);
    }
  };

  const renderBillsTable = (bills: IBill[]) => {
    return (
      <ResponsiveTable>
        <thead>
          <tr>
            <th>{i18n.t('Common:id')}</th>
            <th>{i18n.t('Common:type')}</th>
            <th>{i18n.t('Billing:creationTime')}</th>
            <th>{i18n.t('Billing:dueTime')}</th>
            <th>{i18n.t('Billing:billAmount')}</th>
            <th>{i18n.t('Billing:paymentTime')}</th>
          </tr>
        </thead>
        <tbody>
          {bills.map((bill) => {
            return (
              <tr
                key={'b' + bill.id}
                style={{ cursor: 'pointer' }}
                onClick={() => openModalForBill(bill.id, bill.type)}
              >
                <td>{bill.id}</td>
                <td>{bill.type}</td>
                <td>{bill.creationTimestamp.toDateString(true)}</td>
                <td>
                  {(bill.dueTimestamp || bill.creationTimestamp).toDateString(
                    true,
                  )}
                </td>
                <td style={{ fontWeight: 'bold' }}>{costToPrice(bill.cost)}</td>
                <td>
                  {bill.payment.timestamp
                    ? bill.payment.timestamp.toDateString(true)
                    : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </ResponsiveTable>
    );
  };

  const goToNextPage = () => {
    var currentPage = Number(params.page) || 0;
    var showPaid = params.showPaid == 'paid';
    setTargetUrl(showPaid, currentPage + 1);
  };

  const goToPreviousPage = () => {
    var currentPage = Number(params.page) || 0;
    if (currentPage == 0) return;

    var showPaid = params.showPaid == 'paid';
    setTargetUrl(showPaid, currentPage - 1);
  };

  const toggleShowPaid = () => {
    var currentPage = Number(params.page) || 0;
    var showPaid = params.showPaid === 'paid';

    setTargetUrl(!showPaid, currentPage);
  };

  const setTargetUrl = (showPaid: boolean, page: number) => {
    if (showPaid)
      history.push(Routes.billing(page ? page.toString() : undefined, 'paid'));
    else history.push(Routes.billing(page ? page.toString() : undefined));
  };

  const openModalForBill = (billId: string, type: string) => {
    var currentPage = Number(params.page) || 0;
    var showPaid = params.showPaid == 'paid';

    setLastPage(currentPage);
    setLastShowPaid(showPaid);

    history.push(Routes.bill(type, billId));
  };

  const refundKeyFor = (location: IContainerLocation): string | null => {
    if (!location.containerId || location.locationNumber == null) return null;
    return `${location.containerId}:${location.locationNumber}`;
  };

  const toggleRefundSelection = (key: string) => {
    setSelectedRefundKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const billFullyRefunded =
    billDetails?.payment.state === PaymentStatus.Refunded;
  // Partial refund only makes sense when the bill has more than one container —
  // otherwise it's identical to the whole-bill refund.
  const partialRefundAvailable =
    !!billDetails?.pickpackTransactionId && (locations?.length ?? 0) > 1;

  const getLocationDetails = (location: IContainerLocation) => {
    const container = location.container || {};
    const refundKey = refundKeyFor(location);
    const alreadyRefunded = billFullyRefunded || location.refundedAt != null;
    const isRefundCandidate =
      partialRefundAvailable && refundKey != null && !alreadyRefunded;
    return (
      <div className="card m-2" key={location.id}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>{container.name}</span>
          {isRefundCandidate && (
            <label className="mb-0">
              <input
                type="checkbox"
                checked={selectedRefundKeys.has(refundKey!)}
                onChange={() => toggleRefundSelection(refundKey!)}
                disabled={partialRefundLoading}
              />{' '}
              {i18n.t('Billing:selectForRefund')}
            </label>
          )}
          {alreadyRefunded && (
            <span className="badge bg-success">
              {i18n.t('Billing:refunded')}
            </span>
          )}
        </div>
        <div className="card-body">
          <table className="table">
            <tbody>
              <tr>
                <td>{i18n.t('Billing:ownerSince')}</td>
                <td>{location.ownerSince.toDateString()}</td>
              </tr>
              <tr>
                <td>{i18n.t('Billing:containerDetails')}</td>
                <td>
                  <IconButton
                    label={container.name}
                    tag="a"
                    href={Routes.container(container.id)}
                    icon="🥣"
                  />
                </td>
              </tr>
              <tr>
                <td>{i18n.t('Billing:owner')}</td>
                <td>
                  <IconButton
                    label={
                      (users[location.ownerId] || users[location.nextOwnerId])
                        .username
                    }
                    tag="a"
                    href={Routes.users(
                      (users[location.ownerId] || users[location.nextOwnerId])
                        .id,
                    )}
                    icon={<FaUser />}
                  />
                </td>
              </tr>
              {location.penaltyCost && (
                <tr>
                  <td>{i18n.t('Billing:billAmount')}</td>
                  <td>{costToPrice(location.penaltyCost)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const onPartialRefundClick = async () => {
    if (!billDetails || selectedRefundKeys.size === 0) return;
    if (!window.confirm(i18n.t('Billing:confirmPartialRefund'))) return;

    const containers = Array.from(selectedRefundKeys).map((key) => {
      const [containerId, locationNumber] = key.split(':');
      return {
        container_id: containerId,
        container_location_number: locationNumber,
      };
    });

    try {
      await partialRefundOverduePenaltyBill({
        variables: { bill_id: billDetails.id, containers },
      });
      reloadBillDetails(billDetails.id, billDetails.type);
    } catch (err: any) {
      setPaymentResponse(
        'There was an error capturing the refund attempt. Please verify if the refund was made in Stripe if possible, refresh the page and try again.',
      );
    }
  };

  const payBill = (bill: IBill | null) => {
    if (!bill || bill.payment.state != PaymentStatus.Pending) return;

    setPaymentResponse(null);
    setIsInPaymentProcess(true);
    Communicator.PostData('/Billing/BookPayment', {
      billType: bill.type,
      id: bill.id,
    })
      .then((response) => response.json())
      .then((data: { result: string }) => {
        reloadBillDetails(bill.id, bill.type);
        setPaymentResponse(data.result);
        setIsInPaymentProcess(false);
      })
      .catch((err) => {
        try {
          err.text().then((text: string) => {
            try {
              var data: { result: string } = JSON.parse(text);
              setPaymentResponse(data.result || 'Unkown Error');
            } catch (exception) {
              setPaymentResponse(text || 'Unkown Error');
            }
          });
        } catch (exception: any) {
          setPaymentResponse(exception.toString());
        }
        setIsInPaymentProcess(false);
      });
  };

  const renderBillLocations = (locations: IContainerLocation[]) => {
    return (
      <div>
        <h5>{i18n.t('Common:containers')}</h5>
        {locations.map((l) => getLocationDetails(l))}
      </div>
    );
  };

  const getDetailsModal = () => {
    return (
      <Modal
        isOpen={true}
        size="xl"
        toggle={() => setTargetUrl(lastShowPaid, lastPage)}
      >
        <ModalHeader>
          {i18n.t('Billing:billInformationFor', {
            billId: params.billId,
          })}
        </ModalHeader>
        <ModalBody>
          {billDetails ? (
            <div>
              <h5>{i18n.t('Billing:paymentDetails')}</h5>
              <table className="table">
                <tbody>
                  <tr>
                    <td>Bill Id</td>
                    <td>{billDetails.id}</td>
                  </tr>
                  <tr>
                    <td>Amount</td>
                    <td>{costToPrice(billDetails.cost)}</td>
                  </tr>
                  <tr>
                    <td>Type</td>
                    <td>{params.type}</td>
                  </tr>
                  <tr>
                    <td>{i18n.t('Billing:paymentIdentifier')}</td>
                    <td>{billDetails.payment.paymentIdentifier}</td>
                  </tr>
                  <tr>
                    <td>{i18n.t('Billing:payedAt')}</td>
                    <td>
                      {billDetails.payment.timestamp
                        ? billDetails.payment.timestamp.toDateString(true)
                        : i18n.t('Billing:notPayedYet')}
                    </td>
                  </tr>
                </tbody>
              </table>
              {locations ? renderBillLocations(locations) : null}
              {paymentResponse ? (
                <div
                  style={{
                    background: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    fontFamily: 'monospace',
                  }}
                >
                  {paymentResponse}
                </div>
              ) : null}
              {billDetails.pickpackTransactionId ? (
                <TransactionHistoryTable
                  pickpackTransactionId={billDetails.pickpackTransactionId}
                />
              ) : null}
            </div>
          ) : (
            <h2>Loading</h2>
          )}
        </ModalBody>
        <ModalFooter>
          {billDetails && billDetails.pickpackTransactionId ? (
            <div style={{ marginRight: 'auto' }}>
              <IndicatorButton
                onClick={onRefundWholeBillClick}
                disabled={
                  !transaction || isTransactionRefunded || billFullyRefunded
                }
                isLoading={cancelingTransaction}
              >
                {isTransactionRefunded || billFullyRefunded
                  ? i18n.t('Billing:refunded')
                  : i18n.t('Billing:refundWholeBill')}
              </IndicatorButton>
            </div>
          ) : billDetails && params.type === 'Penalty' ? (
            <Tooltip delayDuration={150}>
              {/* A disabled button fires no pointer events; the wrapper is what
                  shows the tooltip. */}
              <TooltipTrigger asChild>
                <span
                  className="tw:inline-flex tw:cursor-not-allowed"
                  style={{ marginRight: 'auto' }}
                >
                  <Button disabled style={{ pointerEvents: 'none' }}>
                    {i18n.t('Billing:refundWholeBill')}
                  </Button>
                </span>
              </TooltipTrigger>
              {/* Above the reactstrap modal (z-index 1055) */}
              <TooltipContent className="tw:z-[1100] tw:max-w-64">
                {i18n.t('Billing:refundNotPossibleNoTransaction')}
              </TooltipContent>
            </Tooltip>
          ) : null}
          {billDetails && billDetails.payment.state == PaymentStatus.Pending ? (
            <Button
              color="primary"
              onClick={() => payBill(billDetails)}
              disabled={isInPaymentProcess}
            >
              {i18n.t('Billing:startPayment')}
            </Button>
          ) : null}
          {billDetails &&
          params.type === 'Penalty' &&
          !billFullyRefunded &&
          partialRefundAvailable ? (
            <Button
              color="warning"
              onClick={onPartialRefundClick}
              disabled={selectedRefundKeys.size === 0 || partialRefundLoading}
            >
              {i18n.t('Billing:partialRefundSelected', {
                count: selectedRefundKeys.size,
              })}
            </Button>
          ) : null}
          <Button
            color="secondary"
            onClick={() => setTargetUrl(lastShowPaid, lastPage)}
          >
            {i18n.t('Common:close')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  // Effect for initial component mount
  useEffect(() => {
    var page = Number(params.page);
    reloadBills(page || 0, params.showPaid == 'paid', billTypeFilter);
    reloadSubscriptionTypes();
    if (params.billId && params.type)
      reloadBillDetails(params.billId, params.type);
  }, []);

  // Effect for URL parameter changes (page, showPaid)
  useEffect(() => {
    setPaymentResponse(null);
    var page = Number(params.page);
    reloadBills(page || 0, params.showPaid == 'paid', billTypeFilter);
  }, [params.page, params.showPaid, billTypeFilter]);

  // Effect for bill details changes (type, billId)
  useEffect(() => {
    setPaymentResponse(null);
    if (params.billId && params.type)
      reloadBillDetails(params.billId, params.type);
  }, [params.type, params.billId]);

  var currentPage = Number(params.page) || 0;
  var showPaid = params.showPaid == 'paid';
  var showModal = params.billId && params.type;

  return (
    <PageContainer>
      <ContentSection title={i18n.t('Billing:bills')} collapsable={false}>
        <InputGroup className="mb-2">
          <InputGroupText>{i18n.t('Billing:billType')}</InputGroupText>
          <select
            className="form-select"
            name="select"
            id="filterBillType"
            value={billTypeFilter}
            onChange={(e) => changeBillTypeFilter(e.target.value)}
          >
            <option value="DeadlineExtension">
              {i18n.t('Billing:deadlineExtension')}
            </option>
            <option value="Penalty">{i18n.t('Billing:penalty')}</option>
          </select>
        </InputGroup>
        <InputGroup className="mb-2">
          <InputGroupText>{i18n.t('Billing:showPaid')}</InputGroupText>
          <select
            className="form-select"
            name="select"
            id="filterPaid"
            value={showPaid.toString()}
            onChange={toggleShowPaid}
          >
            <option value="true">{i18n.t('Common:yes')}</option>
            <option value="false">{i18n.t('Common:no')}</option>
          </select>
        </InputGroup>
        <div className="d-flex justify-content-between align-items-center mt-5">
          <Button onClick={goToPreviousPage} disabled={!currentPage}>
            Previous
          </Button>
          <Button onClick={goToNextPage}>Next</Button>
        </div>
        {bills.length == 0 && !currentPage ? (
          <p style={{ clear: 'both', marginTop: '1em' }}>
            {i18n.t('Billing:noBills')}
          </p>
        ) : (
          <div style={{ clear: 'both', marginTop: '1em' }}>
            {renderBillsTable(bills)}
          </div>
        )}
        {showModal ? getDetailsModal() : null}
      </ContentSection>
    </PageContainer>
  );
};
